<template>
    <div class="property-details">
        <property-details-loading v-if="loading || !propertyAppResource || !propertyAppResource.exists" />
        <template v-else>
            <b-row class="mb-3">
                <b-col
                        class="d-flex align-items-center col-xxl-12"
                        cols="7"
                >
                    <property-details-navigation :previous-page="propertySearchRoute" />
                </b-col>
                <b-col
                        class="d-xxl-none d-flex align-items-center justify-content-end"
                        cols="5"
                >
                    <i
                            :class="[isFavorite ? 'fas' : 'far', favoriteIsLoading ? 'pointer-events-none' : '']"
                            class="fa-heart cursor-pointer font-size-px-20 color-gray-700"
                            @click="handleFavClick"
                    />
                    <i
                            class="far fa-comments-alt cursor-pointer font-size-px-20  color-gray-700 ml-3"
                            @click="handleDiscussionClick"
                    />
                    <i
                            class="far fa-paperclip cursor-pointer font-size-px-20  color-gray-700 ml-3"
                            @click="handleFilesClick"
                    />
                </b-col>
            </b-row>
            <div class="d-flex justify-content-center align-items-center px-md-3">
                <property-details-photo-gallery
                        :property-app-resource="propertyAppResource"
                        :images="propertyImages"
                        @display="handleOpenPhotoGallery"
                />
            </div>
            <b-container class="mx-auto property-details-container">
                <b-row>
                    <b-col
                            class="mt-2 mt-md-4 mb-4 col-xxl-8"
                            cols="12"
                            lg="8"
                    >
                        <property-details-summary
                                :active-action-buttons="isOffMarket ? [] : activeActionButtons"
                                :property-app-resource="propertyAppResource"
                        />
                        <property-details-attribution
                                v-if="displayDetails"
                                :mls-compliance="mlsCompliance"
                                :property-app-resource="propertyAppResource"
                        />
                        <b-row>
                            <b-col class="mt-4">
                                <property-details-description :property-app-resource="propertyAppResource" />
                            </b-col>
                        </b-row>
                    </b-col>
                    <b-col
                            class="details-actions-col mt-lg-4 mb-lg-4"
                            cols="4"
                    >
                        <property-details-actions
                                :active-action-buttons="isOffMarket ? [] : activeActionButtons"
                                :favorite-option-enabled="favoriteOptionEnabled"
                                :favorite-status="isFavorite"
                                :property-app-resource="propertyAppResource"
                                :is-pending="isPending"
                                :show-real-estate-contact-section="showRealEstateContactSection"
                                :workspace-members-initialized="workspaceMembersInitialized"
                                :get-real-estate-agent="getRealEstateAgent"
                                :is-real-estate-type-excluded-in-organization="isRealEstateTypeExcludedInOrganization"
                                :home-ownership-buy-home-modal="homeOwnershipBuyHomeModal"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <property-details-contact-real-estate-agent
                                v-if="workspaceMembersInitialized"
                                :real-estate-agent="getRealEstateAgent"
                                :workspace-uuid="workspaceUuid"
                                :is-real-estate-type-excluded-in-organization="isRealEstateTypeExcludedInOrganization"
                                :home-ownership-buy-home-modal="homeOwnershipBuyHomeModal"
                                :property-app-resource="propertyAppResource"
                                class="d-xl-none mb-4"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <property-details-footer
                                :mls-compliance="mlsCompliance"
                        />
                    </b-col>
                </b-row>
            </b-container>
            <property-details-modal
                    :favorite-option-enabled="favoriteOptionEnabled"
                    :favorite-status="isFavorite"
            />
        </template>
    </div>
</template>

<script>
    import './PropertyDetails.scss';
    import PropertyDetailsPhotoGallery
        from "./GroupPropertyDetailsPhotoGallery/PropertyDetailsPhotoGallery/PropertyDetailsPhotoGallery";
    import PropertyDetailsAttribution from "./PropertyDetailsAttribution/PropertyDetailsAttribution.vue";
    import PropertyDetailsNavigation from "./PropertyDetailsNavigation/PropertyDetailsNavigation";
    import PropertyDetailsSummary from "./PropertyDetailsSummary/PropertyDetailsSummary";
    import PropertyDetailsActions from "./PropertyDetailsActions/PropertyDetailsActions";
    import PropertyDetailsDescription from "./PropertyDetailsDescription/PropertyDetailsDescription";
    import PropertyDetailsFooter from "./PropertyDetailsFooter/PropertyDetailsFooter";
    import PropertyDetailsModal, {
        PROPERTY_DETAILS_MODAL_DISPLAY,
        TABS
    } from "./PropertyDetailsModal/PropertyDetailsModal";
    import {GLOBAL_EVENTS} from "../../../helpers/globalEvents";
    import FavoriteButtonMixin, {PROPERTY_DETAILS_FAVORITE_ACTION} from "../../mixins/FavoriteButtonMixin";
    import ShowNotificationMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/ShowNotificationMixin";
    import {ApiFilter, AppResourceSearch} from "@milestoneslabs/milestones-js-engine";
    import {
        PENDING_AND_UNDER_CONTRACT
    } from "@milestoneslabs/milestones-core-js/src/AppResources/LuListingStatusAppResource";
    import {store} from "@milestoneslabs/milestones-property-search";
    import {
        REAL_ESTATE_AGENT
    } from "@milestoneslabs/milestones-core-js/src/AppResources/LuWorkspaceMembershipTypeAppResource";
    import PropertyDetailsContactRealEstateAgent
        from './PropertyDetailsActions/PropertyDetailsContactRealEstateAgent/PropertyDetailsContactRealEstateAgent';
    import axios from "axios";
    import {mapGetters} from "vuex";
    import {
        TYPE
    } from "@milestoneslabs/milestones-core-js/src/AppResources/WorkspaceTypeAppResource";
    import PropertyDetailsLoading from './PropertyDetailsLoading';

    export default {
        name: "PropertyDetails",
        components: {
            PropertyDetailsModal,
            PropertyDetailsFooter,
            PropertyDetailsDescription,
            PropertyDetailsActions,
            PropertyDetailsSummary,
            PropertyDetailsNavigation,
            PropertyDetailsPhotoGallery,
            PropertyDetailsContactRealEstateAgent,
            PropertyDetailsAttribution,
            PropertyDetailsLoading,
        },
        mixins: [
            FavoriteButtonMixin,
            ShowNotificationMixin,
        ],
        props: {
            propertySearchRoute: {
                type: [String, Object],
                default: 'property.search',
            },
            activeActionButtons: {
                type: Array,
                default: () => [
                    'tour',
                    'offer',
                    'finance',
                    'trade',
                ]
            },
            showRealEstateContactSection: {
                type: Boolean,
                default: false,
            },
            homeOwnershipBuyHomeModal: {
                type: String,
                required: false,
            },
            propertyAppResource: {
                type: Object,
                required: true,
            },
            propertyImages: {
                type: Array,
                required: true
            },
        },
        computed: {
            ...mapGetters({
                project: 'getCurrentWorkspace',
            }),
            displayDetails() {
                return !this.propertyAppResource.isSold &&
                    [TYPE.HOME_PURCHASE, TYPE.HOME_OWNERSHIP].includes(this.project.workspaceType.type);
            },
            isOffMarket() {
                /* after the update on milestones engine this field comes in requests snake case*/
                return !!this.propertyAppResource.offMarketAt || !!this.propertyAppResource.off_market_at;
            },

            propertyRouteUuid() {
                return this.$route.params.id;
            },

            isPending() {
                if (!this.propertyAppResource || !this.hjPendingListingStatuses.length) {
                    return false;
                }
                return this.hjPendingListingStatuses.includes(this.propertyAppResource.status);
            },
            realEstateAgents() {
                return this.workspaceMembers.filter((member) => {
                    return member.workspaceMembershipType.slug === REAL_ESTATE_AGENT;
                });
            },
            getRealEstateAgent() {
                if (this.realEstateAgents.length > 0) {
                    return this.realEstateAgents[0];
                }

                return undefined;
            },
            workspaceUuid() {
                return store.getters.getWorkspaceUuid;
            },
            imagesCount() {
                return this.propertyImages.length;
            }
        },

        watch: {
            isPending() {
                this.propertyAppResource.isPending = this.isPending;
            }
        },

        data() {
            return {
                favorite: null,
                favoriteIsLoading: false,
                loading: false,
                hjPendingListingStatuses: [],
                workspaceMembersAppResourceSearch: new AppResourceSearch('workspace-organization-member'),
                workspaceMembers: [],
                workspaceMembersInitialized: false,
                serviceProviderTypeAppResourceSearch: new AppResourceSearch('service-provider-type'),
                isRealEstateTypeExcludedInOrganization: false,
                mlsCompliance: null,
            };
        },

        methods: {
            async getDetailsRules() {
                try {
                    this.mlsCompliance = await axios.post(
                        `${window.env.MILESTONES_API_HOST}/api/v1/mls/details-rules`,
                        {
                            propertyUuid: this.propertyAppResource.uuid,
                            mlsDefinitionUuid: this.project.mlsDefinitionUuid,
                            workspaceUuid: this.workspaceUuid,
                            propertyDetailsListingPage: true,
                        },
                    );
                } catch (e) {
                    this.showNotification({
                        type: 'error',
                        message: 'There was an error. Please try again later!'
                    });
                }
            },
            async fetchHjListingStatuses() {
                let hjListingStatusAppResourceSearch = new AppResourceSearch('home-junction-listing-status');
                hjListingStatusAppResourceSearch.setSearchData({
                    pagination: 'disabled',
                    filter: new ApiFilter({
                        params: {
                            lookupListingStatusUuid: PENDING_AND_UNDER_CONTRACT
                        }
                    })
                }, false);
                try {
                    await hjListingStatusAppResourceSearch.search();
                    this.hjPendingListingStatuses = hjListingStatusAppResourceSearch
                        .appResourceCollection
                        .toArray()
                        .map(item => item.status);
                } catch (e) {
                    console.error(e);
                    this.showNotification({
                        message: 'Something went wrong. Please try again later',
                        type: 'error'
                    });
                }
            },

            handleOpenPhotoGallery(index) {
                this.$bus.$emit(
                    PROPERTY_DETAILS_MODAL_DISPLAY,
                    {
                        property: this.propertyAppResource,
                        tab: this.imagesCount ? TABS.PHOTOS : TABS.MAP,
                        imageIndex: index,
                    }
                );
            },

            handleDiscussionClick() {
                this.$bus.$emit(GLOBAL_EVENTS.PROPERTY_DETAILS.DISCUSSION_CLICK);
            },

            handleFilesClick() {
                this.$bus.$emit(GLOBAL_EVENTS.PROPERTY_DETAILS.FILES_CLICK);
            },

            handleFavoriteAction(favorite) {
                this.favorite = favorite;
            },

            async fetchWorkspaceMembers() {
                await this.workspaceMembersAppResourceSearch.setSearchData({
                    pagination: 'disabled',
                    include: [
                        'user',
                        'workspaceMembershipType',
                    ],
                    filter: new ApiFilter({
                        params: {
                            workspaceUuid: this.workspaceUuid,
                        },
                    }),
                    orderBy: {
                        createdAt: 'asc',
                    },
                }, false);

                try {
                    await this.workspaceMembersAppResourceSearch.search();

                    this.workspaceMembers = this.workspaceMembersAppResourceSearch.appResourceCollection.toArray();
                    this.workspaceMembersInitialized = true;
                } catch (e) {
                    console.error(e);
                }
            },

            async fetchOrganizationHasRealEstateTypeExcluded() {
                await this.serviceProviderTypeAppResourceSearch.setSearchData({
                    pagination: 'disabled',
                    limit: 1,
                    include: [
                        'excludedInOrganizations',
                    ],
                    filter: new ApiFilter({
                        params: {
                            title: 'Real Estate Agents',
                        },
                        relations: {
                            excludedInOrganizations: new ApiFilter({
                                params: {
                                    uuid: store.getters.getOrganizationUuid,
                                },
                            }),
                        }
                    }),
                }, false);

                try {
                    await this.serviceProviderTypeAppResourceSearch.search();

                    this.isRealEstateTypeExcludedInOrganization =
                        !this.serviceProviderTypeAppResourceSearch.appResourceCollection.isEmpty;
                } catch (e) {
                    console.error(e);
                }
            },
        },

        async beforeMount() {
            this.loading = true;
            //ToDo these requests are crap, must be fixed, the page must wait only for fetchProperty request
            await this.fetchHjListingStatuses();
            this.$bus.$on(PROPERTY_DETAILS_FAVORITE_ACTION, this.handleFavoriteAction);
            await this.fetchWorkspaceMembers();
            await this.fetchOrganizationHasRealEstateTypeExcluded();
            await this.getDetailsRules();
            this.$nextTick(() => this.loading = false);
        },

        beforeDestroy() {
            this.$bus.$off(PROPERTY_DETAILS_FAVORITE_ACTION, this.handleFavoriteAction);
        }
    };
</script>
